<template>
    <div>
        <Panel
            :title="$t('LINK Service Definitions')"
            :add-button="$store.hasRight('EDIT_LINK') ? $t('Add New LINK') : ''"
            :delete-button="selected.length > 0"
            @delete="deleteLinks"
            @input="openForm"
        >
            <table id="linkdefinitions_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('LINK Name') }}</th>
                        <template v-if="$store.hasRight('EDIT_LINK')">
                            <th></th>
                            <th></th>
                            <th></th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="link in links">
                        <td class="primary">
                            {{ link.name }}
                        </td>
                        <template v-if="$store.hasRight('EDIT_LINK')">
                            <td>
                                <cic-button class="edit-link-button" @click="openForm(link.linkId)">
                                    {{ $t('Edit LINK') }}
                                </cic-button>
                            </td>
                            <td>
                                <cic-button class="edit-ui-button" @click="openEditUIForm(link.linkId)">
                                    {{ $t('Edit UI') }}
                                </cic-button>
                            </td>
                            <td>
                                <cic-checkbox
                                    :value="selected.indexOf(link.linkId) !== -1"
                                    @click="handleSelected(link.linkId)"
                                />
                            </td>
                        </template>
                    </tr>
                </tbody>
            </table>
            <template #footer>
                <cic-button id="editDefaultUIButton" class="edit-default-ui-button" @click="openEditUIForm">
                    {{ $t('Edit Default UI') }}
                </cic-button>
            </template>
        </Panel>

        <EditLinkDefinition
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            :queues="queues"
            :groups="agentGroups"
            :email-addresses="emailAddresses"
            :sms-clis="smsClis"
            :language-options="languageOptions"
            @blur="closePopup"
            @input="saveLinkDefinitionItem"
            @change="updateFieldValue"
        />

        <EditLinkDefinitionStyle
            v-if="styleItem !== null && !$store.showLoadingOverlay"
            :item="styleItem"
            @blur="closePopup"
            @input="saveLinkStyleItem"
            @change="updateStyleFieldValue"
        />
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { load, save, deleteItems } from '@/utils/Http';
    import EditLinkDefinition from './EditLinkDefinition';
    import EditLinkDefinitionStyle from './EditLinkDefinitionStyle';
    import './styles/linkDefinitions.css';

    export default {
        name: 'LinkDefinitions',
        components: {
            CicButton,
            CicCheckbox,
            Panel,
            EditLinkDefinition,
            EditLinkDefinitionStyle,
        },
        mixins: [TableUtils],
        data() {
            return {
                links: [],
                page: 1,
                item: null,
                styleItem: null,
                selected: [],
                queues: [],
                emailAddresses: [],
                smsClis: [],
                agentGroups: [],
                languageOptions: [],
            };
        },
        created() {
            this.loadTable();
        },

        methods: {
            closePopup() {
                this.item = null;
                this.styleItem = null;
            },

            loadTable() {
                this.item = null;
                this.styleItem = null;
                this.selected = [];
                load('linkdefinitions/get', {}, (response) => {
                    this.links = response.links;
                    this.languageOptions = response.languageOptions;
                });
            },

            openForm(linkId) {
                if (linkId > 0) {
                    load('linkdefinition/get', { linkId }, (response) => {
                        this.item = {
                            linkId: response.link.linkId,
                            language: response.link.language,
                            name: response.link.name,
                            messageTemplate: response.link.messageTemplate,
                            linkTypes: response.link.linkTypes,
                            smsOptionSelected: response.link.sendOptions.includes(1),
                            emailOptionSelected: response.link.sendOptions.includes(2),
                            copyPasteOptionSelected: response.link.sendOptions.includes(3),
                            cli: response.link.cli,
                            emailAddress: response.link.fromAddress,
                            queueIds: response.assignedQueues,
                            agentGroupIds: response.assignedAgentGroups,
                            unsolicitedLink: response.link.unsolicitedLink,
                        };
                    });
                } else {
                    this.item = {
                        linkId: null,
                        language: this.languageCode(),
                        name: '',
                        messageTemplate: '',
                        linkTypes: [],
                        smsOptionSelected: false,
                        emailOptionSelected: false,
                        copyPasteOptionSelected: false,
                        cli: '',
                        emailAddress: '',
                        queueIds: [],
                        agentGroupIds: [],
                        unsolicitedLink: false,
                    };
                }

                if (this.queues.length === 0) {
                    load('linkdefinitions/getAllQueues', {}, (response) => {
                        this.queues = response.allQueues;
                    });
                }

                if (this.agentGroups.length === 0) {
                    load('linkdefinitions/getAllAgentGroups', {}, (response) => {
                        this.agentGroups = response.allAgentGroups;
                    });
                }

                if (this.emailAddresses.length === 0) {
                    load('linkdefinition/getEmailAddresses', {}, (response) => {
                        this.emailAddresses = response.emailAddresses;
                    });
                }

                if (this.smsClis.length === 0) {
                    load('linkdefinition/getSmsClis', {}, (response) => {
                        this.smsClis = response.smsClis;
                    });
                }
            },

            openEditUIForm(linkId = 0) {
                load('linkdefinitionstyle/get', { linkId }, (response) => {
                    this.styleItem = {
                        linkId: linkId > 0 ? linkId : undefined,
                        logo: response.style.logo,
                        headerTitle: response.style.headerTitle,
                        headerBackgroundColour: response.style.headerBackgroundColour,
                        headerTextColour: response.style.headerTextColour,
                        buttonColour: response.style.buttonColour,
                        buttonTextColour: response.style.buttonTextColour,
                        fontFamily: response.style.fontFamily,
                    };
                });
            },

            saveLinkDefinitionItem() {
                if (this.item.name.trim() === '') {
                    alert(this.$t('Name is a required field'));
                    return;
                }
                if (this.item.messageTemplate.trim() === '') {
                    alert(this.$t('Message Template is a required field'));
                    return;
                }
                if (
                    !this.item.copyPasteOptionSelected &&
                    !this.item.smsOptionSelected &&
                    !this.item.emailOptionSelected
                ) {
                    alert(this.$t('You must select a method to share a message or allow agent to copy link'));
                    return;
                }
                if (this.item.linkTypes.length === 0) {
                    alert(this.$t('You must select at least one Link Type'));
                    return;
                }
                if (this.item.smsOptionSelected && this.item.cli === '') {
                    alert(this.$t('From SMS CLI is a required field'));
                    return;
                }
                if (this.item.emailOptionSelected && this.item.emailAddress === '') {
                    alert(this.$t('From Email Address is a required field'));
                    return;
                }

                save(
                    'linkdefinition/update',
                    {
                        ...this.item,
                        smsOptionSelected: this.item.smsOptionSelected ? 1 : 0,
                        emailOptionSelected: this.item.emailOptionSelected ? 1 : 0,
                        copyPasteOptionSelected: this.item.copyPasteOptionSelected ? 1 : 0,
                        queueIds: JSON.stringify(this.item.queueIds),
                        agentGroupIds: JSON.stringify(this.item.agentGroupIds),
                        unsolicitedLink: this.item.unsolicitedLink ? 1 : 0,
                    },
                    () => {
                        this.loadTable();
                    },
                );
            },

            saveLinkStyleItem() {
                if (!this.validHex(this.styleItem.headerBackgroundColour)) {
                    alert(this.$t('Header Background Colour must be a valid hex code'));
                    return;
                }

                if (!this.validHex(this.styleItem.headerTextColour)) {
                    alert(this.$t('Header Text Colour must be a valid hex code'));
                    return;
                }

                if (!this.validHex(this.styleItem.buttonColour)) {
                    alert(this.$t('Button Colour must be a valid hex code'));
                    return;
                }

                if (!this.validHex(this.styleItem.buttonTextColour)) {
                    alert(this.$t('Button Text Colour must be a valid hex code'));
                    return;
                }

                save('linkdefinition/saveLinkStyle', this.styleItem, () => {
                    this.loadTable();
                });
            },

            deleteLinks() {
                deleteItems('linkdefinitions/delete', { selectedIds: JSON.stringify(this.selected) }, () => {
                    this.loadTable();
                });
            },

            updateStyleFieldValue(field, value) {
                let item = { ...this.styleItem };
                item[field] = value;
                this.styleItem = item;
            },

            validHex(colour) {
                return colour.trim() === '' || /^#[0-9a-fA-F]{6}$/.test(colour);
            },

            languageCode() {
                const codes = this.languageOptions.map((lang) => lang.languageCode);
                if (codes.includes(this.$i18n.locale)) {
                    return this.$i18n.locale;
                }

                return 'en-GB';
            },
        },
    };
</script>
